// Pure mapping from the review screen's editable rows to the resolved program
// the apply step consumes. Kept free of service imports so the unit tests can
// load it without pulling the Angular/service dependency graph into mocha —
// the draft mutation itself lives in workout-plan-scanner-apply.ts.

import { getRepTargetForIntensity, getMinTargetRepsForIntensity, IntensityLevel } from '@cf-shared/data-model/fitness-plan';

export interface ResolvedProgramExercise {
  /** existing exercise to use — set unless `createCustom` is */
  exerciseId?: string;
  /** create a custom exercise at apply time (owner decision 2026-06-11): used for rows
   *  with no match/suggestion, and for rows where the user rejected the suggestion to
   *  keep the exercise exactly as scanned. `similarExerciseId` carries the rejected
   *  suggestion for muscle targeting (the edit-exercise "similar exercise" property). */
  createCustom?: {
    name: string;
    similarExerciseId?: string;
  };
  sets: number;
  targetReps?: number;
}

export interface ResolvedProgramDay {
  name: string;
  exercises: ResolvedProgramExercise[];
}

export interface ResolvedProgram {
  days: ResolvedProgramDay[];
}

/** The subset of a review row the mapping needs (mirrors the page's ReviewExercise). */
export interface ReviewRowInput {
  resolvedExerciseId: string | null;
  /** the exercise name exactly as scanned — becomes the custom exercise's name */
  scannedName: string;
  /** user rejected the suggestion and wants the exercise as scanned (custom creation) */
  useScannedAsCustom?: boolean;
  /** the suggested/fallback exercise the row was pre-filled with — carried onto the
   *  created custom as similarExerciseId when the user rejects it */
  suggestionId?: string;
  included: boolean;
  /** numeric inputs from the review screen; targetReps 0 = no explicit target */
  sets: number;
  targetReps: number;
}

export interface ReviewDayInput {
  name: string;
  included: boolean;
  exercises: ReviewRowInput[];
}

/** When a sets input is cleared or unparseable, fall back to the most common
 *  working-set count rather than dropping the row the user explicitly included. */
export const FALLBACK_SETS_WHEN_UNPARSEABLE = 3;
const MAX_SETS = 99;
const MAX_REPS = 99;

/** Map the review screen's editable rows to the resolved program apply consumes:
 *  excluded days/rows are dropped; rows resolved to an existing exercise carry its
 *  id; rows without one (unmatched, or suggestion rejected by the user) become
 *  create-custom entries named the scanned name (owner decision 2026-06-11), with
 *  the rejected suggestion as similarExerciseId. The numeric inputs are
 *  clamped, and a duplicate WITHIN one day collapses onto its first
 *  occurrence (one subslot cannot appear twice in the same workout). */
export function reviewRowsToResolvedProgram(days: ReviewDayInput[]): ResolvedProgram {
  const resolvedDays: ResolvedProgramDay[] = [];
  for (const day of days) {
    if (!day.included) continue;
    const exercises: ResolvedProgramExercise[] = [];
    const seenKeys = new Set<string>();
    for (const row of day.exercises) {
      if (!row.included) continue;

      // an existing exercise is used unless the user explicitly chose the scanned name
      const useExistingId = !row.useScannedAsCustom && row.resolvedExerciseId ? row.resolvedExerciseId : undefined;
      // in-day dedup: existing exercises by id, customs by normalized scanned name
      const key = useExistingId ?? `custom:${row.scannedName.trim().toLocaleLowerCase()}`;
      if (seenKeys.has(key)) continue;
      seenKeys.add(key);

      const exercise: ResolvedProgramExercise = {
        sets: clampInt(row.sets, 1, MAX_SETS) ?? FALLBACK_SETS_WHEN_UNPARSEABLE,
      };
      if (useExistingId) {
        exercise.exerciseId = useExistingId;
      } else {
        exercise.createCustom = { name: row.scannedName.trim() };
        // the rejected suggestion still serves as the muscle-targeting reference
        if (row.suggestionId) {
          exercise.createCustom.similarExerciseId = row.suggestionId;
        }
      }
      const reps = clampInt(row.targetReps, 1, MAX_REPS);
      if (reps !== undefined) {
        exercise.targetReps = reps;
      }
      exercises.push(exercise);
    }
    // a day whose rows were all excluded adds nothing to the plan
    if (exercises.length === 0) continue;
    resolvedDays.push({ name: day.name, exercises });
  }
  return { days: resolvedDays };
}

/** Clamp a numeric input to an integer in [min, max], or undefined when
 *  unparseable / non-positive (0 = "no value" for the optional reps input). */
function clampInt(value: number, min: number, max: number): number | undefined {
  if (!isFinite(value) || value <= 0) return undefined;
  return Math.max(min, Math.min(max, Math.round(value)));
}

/** One intensity level of a (possibly periodized) exercise: its level id and the
 *  intensity percentage the plan assigns it (drives the level's ideal rep target). */
export interface IntensityLevelInput {
  intensityLevel: IntensityLevel;
  intensityPct: number;
}

/** Distribute ONE scanned rep target across an exercise's intensity levels.
 *
 *  A scanned plan states a single rep target, but a periodized exercise (DUP = heavy
 *  + light, TUP = heavy + medium + light) needs a different target per session. Naively
 *  writing the scanned number onto one fixed level breaks the spacing the levels
 *  require (e.g. heavy 10 next to light 13 when they must be ≥4 apart) — owner report
 *  2026-06-13.
 *
 *  Instead: each level has an IDEAL target from its intensity % (`getRepTargetForIntensity`).
 *  Pick the level whose ideal is CLOSEST to the scanned value as the anchor, set it to
 *  exactly the scanned value, and shift every other level by that same offset — so the
 *  levels keep their natural spacing while honoring what the user wrote. Each result is
 *  floored at the level's minimum. A single-level (non-periodized) exercise simply maps
 *  that level to the scanned value. Returns [] when no levels are given.
 */
export function distributeRepTargetAcrossLevels(
  levels: IntensityLevelInput[],
  scannedTargetReps: number,
): { intensityLevel: IntensityLevel; targetReps: number }[] {
  if (levels.length === 0) return [];
  const withIdeal = levels.map(l => ({
    intensityLevel: l.intensityLevel,
    ideal: getRepTargetForIntensity(l.intensityPct),
  }));
  let anchor = withIdeal[0];
  for (const l of withIdeal) {
    if (Math.abs(l.ideal - scannedTargetReps) < Math.abs(anchor.ideal - scannedTargetReps)) {
      anchor = l;
    }
  }
  const offset = scannedTargetReps - anchor.ideal;
  // shift every level by the same offset, then floor each at its own minimum. Flooring
  // each level INDEPENDENTLY is safe only because the per-level minimums
  // (getMinTargetRepsForIntensity) are themselves spaced at least as far apart as the
  // sibling-gap rule (DUP_REP_TARGET_GAP, TUP_REP_TARGET_GAP in getSiblingRepTargetBounds).
  // If those minimums or that gap rule ever change, re-check that flooring cannot shrink
  // two adjacent levels below the required spacing.
  return withIdeal.map(l => ({
    intensityLevel: l.intensityLevel,
    targetReps: Math.max(getMinTargetRepsForIntensity(l.intensityLevel), l.ideal + offset),
  }));
}

/** The intensity level to rebuild paths at so the exercise's CURRENT periodization is
 *  preserved: any DUP-range level rebuilds the heavy+light pair, any TUP-range level the
 *  heavy+medium+light triple, otherwise a single Initial path (see rebuildPathsIfNeeded
 *  in workout-loader.service.ts, which keys the path count off this value's range). */
export function periodizationRebuildLevel(activeLevels: IntensityLevel[]): IntensityLevel {
  if (activeLevels.some(l => l >= IntensityLevel.TUPlight && l <= IntensityLevel.TUPheavy)) {
    return IntensityLevel.TUPheavy;
  }
  if (activeLevels.some(l => l >= IntensityLevel.DUPlight && l <= IntensityLevel.DUPheavy)) {
    return IntensityLevel.DUPheavy;
  }
  return IntensityLevel.Initial;
}
